// VideoThumbnail.jsx - Simple thumbnail with blob cache + eager server fallback
import React, { useEffect, useState } from "react";
import { getBlobUrl, extractThumbnailUrl } from "../utils/thumbnailPreloader";

const ThumbnailLoadingIndicator = ({ className = "w-full h-full", style }) => (
  <div
    className={`${className} overflow-hidden bg-black`}
    style={style}
    aria-hidden="true"
  >
    <div className="w-full h-full video-loading-pulse" />
  </div>
);

const VideoThumbnail = ({
  video,
  alt,
  className = "w-full h-full object-cover",
  showGradientFallback = true,
  // Eager by default, deliberately. Flipping this to "lazy" was measured and bought nothing
  // (Home went 14,782 KB -> 14,906 KB, i.e. noise) because Home preloads its thumbnails
  // through a JS fetch that ignores the attribute entirely, and the page's real weight is a
  // handful of oversized SVG/PNG assets rather than thumbnails. All it did was put every
  // caller one forgotten prop away from a card that paints as an empty box. Deferral is
  // opt-IN, per list, where the list is unbounded — see WorkspacePage.
  loading = "eager",
  fetchPriority = "auto",
  decoding = "async",
}) => {
  // Prefer cached blob URL (instant), fall back to server URL
  const blobUrl = getBlobUrl(video);
  const src = blobUrl || extractThumbnailUrl(video);

  const [failed, setFailed] = useState(false);
  // If blob is cached, start as loaded (no skeleton flash)
  const [loaded, setLoaded] = useState(!!blobUrl);

  useEffect(() => {
    setLoaded(!!getBlobUrl(video));
    setFailed(false);
  }, [src]);

  // Skip placeholder thumbnails
  if (
    video.thumbnail_url &&
    video.thumbnail_url.includes("default_thumbnails")
  ) {
    if (showGradientFallback) {
      return <ThumbnailLoadingIndicator className={className} />;
    }
    return null;
  }

  if (!src || failed) {
    if (showGradientFallback) {
      return <ThumbnailLoadingIndicator className={className} />;
    }
    return null;
  }

  // Keep the slot black until the image has decoded, then fade in.
  return (
    <div className="relative w-full h-full overflow-hidden bg-black">
      <ThumbnailLoadingIndicator
        className="absolute inset-0 z-10 transition-opacity duration-200 ease-out"
        style={{
          opacity: loaded ? 0 : 1,
        }}
      />
      <img
        src={src}
        alt={alt}
        className={`${className} relative z-20`}
        style={{
          opacity: loaded ? 1 : 0,
          transition: loaded
            ? "opacity 260ms ease 120ms"
            : "opacity 120ms ease",
        }}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding={decoding}
        onLoad={() => {
          requestAnimationFrame(() => setLoaded(true));
        }}
        onError={() => setFailed(true)}
      />
    </div>
  );
};

export default VideoThumbnail;
